import React from 'react';

import { ValueBox, ValueGroup, ValueToggle } from '@candu-ai/craft/ui';
import { useMultiNodes } from 'craft/hooks';
import { getBreakpointIndex, useViewportBreakpoint } from 'craft/state';
import { CommonNodeProperties, MediaBreakpoint, MediaBreakpoint as Breakpoints } from 'craft/types';

interface Props {
  nodeIds: string[];
}

const breakpoints = [
  {
    breakpoint: Breakpoints.SM,
    tooltip: (action: string) => `Component will be ${action} on displays smaller than 700px`,
  },
  {
    breakpoint: Breakpoints.MD,
    tooltip: (action: string) => `Component will be ${action} on displays between 700px and 1200px`,
  },
  {
    breakpoint: Breakpoints.LG,
    tooltip: (action: string) => `Component will be ${action} on displays greater than 1200px`,
  },
];

const toggleBreakpoint = (breakpoint: MediaBreakpoint, hidden: MediaBreakpoint[]) => {
  const hasBreakpoint = hidden.includes(breakpoint);
  if (hasBreakpoint) {
    return hidden.filter((bp: Breakpoints) => bp !== breakpoint);
  }
  return [...hidden, breakpoint];
};

export const MediaBreakpoints = ({ nodeIds }: Props) => {
  const {
    media: mediaOrUndefined,
    actions: { setCustom },
  } = useMultiNodes(
    nodeIds,
    (node) => ({ media: node?.data.custom?.media ?? {} }),
    CommonNodeProperties.Multiple,
  );

  // get currently active breakpoint
  const activeBreakpoint = useViewportBreakpoint();

  // Guards against crash where media can be undefined (found in sentry logs)
  const media = mediaOrUndefined || {};
  const hiddenMedia = media.hidden || [];

  const isHidden = hiddenMedia.indexOf(activeBreakpoint) > -1;

  const getTooltipText = (action: string) => {
    const breakpointTooltip = breakpoints.find(({ breakpoint }) => breakpoint === activeBreakpoint)
      ?.tooltip;
    return breakpointTooltip?.(action);
  };

  const toggleBreakpointVisibility = () => {
    const currentBreakpointIndex = getBreakpointIndex(activeBreakpoint);
    const breakpointsAboveExistingOne = breakpoints.filter(
      (bk) => getBreakpointIndex(bk.breakpoint) >= currentBreakpointIndex,
    );
    const breakpointsToToggle = breakpointsAboveExistingOne.filter(
      (bk) => hiddenMedia.includes(bk.breakpoint) === isHidden,
    );
    let hidden: MediaBreakpoint[] = [...hiddenMedia];
    breakpointsToToggle.forEach((bk) => {
      hidden = toggleBreakpoint(bk.breakpoint, hidden);
    });

    setCustom({
      media: {
        ...media,
        // sort the viewport so we can run tests cleanly...
        hidden: hidden.sort((a, b) => getBreakpointIndex(a) - getBreakpointIndex(b)),
      },
    });
  };

  return (
    <ValueGroup>
      <ValueBox data-cy="media-breakpoints" responsiveToggle>
        <ValueToggle
          toggleGroup="responsive-display"
          id="responsive-display-show"
          data-cy={`media-breakpoint-${activeBreakpoint}-show`}
          onClick={toggleBreakpointVisibility}
          isActive={!isHidden}
          label="Show"
          color="default"
          tooltip={isHidden ? getTooltipText('shown') : undefined}
        />
        <ValueToggle
          toggleGroup="responsive-display"
          id="responsive-display-hide"
          data-cy={`media-breakpoint-${activeBreakpoint}-hide`}
          onClick={toggleBreakpointVisibility}
          isActive={isHidden}
          label="Hide"
          color="default"
          tooltip={!isHidden ? getTooltipText('hidden') : undefined}
        />
      </ValueBox>
    </ValueGroup>
  );
};
